import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Grid, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react/dist/iconify.js';
import { LEVEL_LABELS, STAGE_FIELDS } from '../constants/stageFields';
import { RT } from '../theme/retentionTheme';
import { IconBadge, SoftChip } from './ui';

// Renders a stage's form from constants/stageFields.js. `values` = what the API returned; only changed keys are
// passed to onSave. `readOnly` shows the form as a preview (the stage's save API isn't built yet).

const toInput = (type, v) => {
  if (v === null || v === undefined) return '';
  if (type === 'datetime') return String(v).replace(' ', 'T').slice(0, 16);
  if (type === 'date') return String(v).slice(0, 10);
  return v;
};

const FieldLabel = ({ field, prefilled }) => (
  <Stack direction="row" alignItems="center" gap={0.75} mb={0.75}>
    <Typography variant="caption" sx={{ fontWeight: 700, color: RT.ink2 }}>
      {field.label}
      {field.required && (
        <Box component="span" sx={{ color: RT.error, ml: 0.25 }}>
          *
        </Box>
      )}
    </Typography>
    {field.kyc && (
      <Tooltip title={`KYC · ${field.kyc}`}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#7C3AED' }} />
      </Tooltip>
    )}
    {prefilled && <SoftChip label="prefilled" icon="solar:magic-stick-3-bold" color={RT.info} sx={{ height: 18, fontSize: 10 }} />}
  </Stack>
);

const Pills = ({ options, value, onChange, multi, disabled }) => {
  const selected = multi ? String(value || '').split(',').filter(Boolean) : [value];
  const toggle = (v) => {
    if (!multi) return onChange(value === v ? null : v);
    const next = selected.includes(String(v)) ? selected.filter((s) => s !== String(v)) : [...selected, String(v)];
    return onChange(next.join(','));
  };
  return (
    <Stack direction="row" gap={0.75} flexWrap="wrap">
      {options.map((opt) => {
        const on = multi ? selected.includes(String(opt.value)) : String(value) === String(opt.value) && value !== null && value !== '';
        return (
          <Chip
            key={opt.value}
            label={opt.label}
            size="small"
            disabled={disabled}
            onClick={() => toggle(opt.value)}
            icon={on ? <Icon icon="solar:check-circle-bold" width={14} /> : undefined}
            sx={{
              height: 30,
              px: 0.25,
              fontWeight: 700,
              cursor: 'pointer',
              border: `1px solid ${on ? RT.primary : RT.line}`,
              bgcolor: on ? RT.primary : '#fff',
              color: on ? '#fff' : RT.ink2,
              '& .MuiChip-icon': { color: '#fff' },
              '&:hover': { bgcolor: on ? RT.primaryDark : RT.primaryLight },
            }}
          />
        );
      })}
    </Stack>
  );
};

const Level = ({ value, onChange, disabled }) => (
  <Stack direction="row" alignItems="center" gap={0.75}>
    {[1, 2, 3, 4, 5].map((n) => {
      const on = Number(value) >= n;
      return (
        <Tooltip key={n} title={LEVEL_LABELS[n - 1]}>
          <Box
            component="button"
            type="button"
            disabled={disabled}
            onClick={() => onChange(Number(value) === n ? null : n)}
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              border: `1px solid ${on ? 'transparent' : RT.line}`,
              background: on ? `linear-gradient(135deg, ${RT.primary}, #7C3AED)` : '#fff',
              color: on ? '#fff' : RT.muted,
              fontWeight: 800,
              fontSize: 13,
              cursor: disabled ? 'default' : 'pointer',
              transition: 'transform .1s',
              '&:hover': disabled ? {} : { transform: 'translateY(-1px)' },
            }}
          >
            {n}
          </Box>
        </Tooltip>
      );
    })}
    <Typography variant="caption" sx={{ ml: 0.5, color: RT.muted, fontWeight: 700, minWidth: 64 }}>
      {value ? LEVEL_LABELS[Number(value) - 1] : ''}
    </Typography>
  </Stack>
);

const display = (field, value) => {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) return value.length ? value.join(' · ') : 'None';
  return field.suffix ? `${value}${field.suffix}` : value;
};

const ReadOnly = ({ field, value: raw }) => {
  const value = display(field, raw);
  return (
  <Box sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: '#F4F6FA', border: `1px dashed ${RT.line}`, minHeight: 40 }}>
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, color: value ? RT.ink : RT.faint }}>
        {value ?? '—'}
      </Typography>
      <SoftChip
        label={field.source === 'auto' ? 'auto' : 'CRM'}
        icon={field.source === 'auto' ? 'solar:bolt-bold' : 'solar:hand-shake-bold'}
        color={field.source === 'auto' ? RT.info : RT.muted}
        sx={{ height: 20, fontSize: 10.5 }}
      />
    </Stack>
  </Box>
  );
};

function FieldInput({ field, value, onChange, disabled, dynamicOptions }) {
  const options = field.optionsFrom ? dynamicOptions?.[field.optionsFrom] || [] : field.options || [];
  switch (field.type) {
    case 'readonly':
      return <ReadOnly field={field} value={value} />;
    case 'chips':
      return options.length ? (
        <Pills options={options} value={value} onChange={onChange} disabled={disabled} />
      ) : (
        <Typography variant="caption" sx={{ color: RT.faint }}>
          No options yet
        </Typography>
      );
    case 'multi':
      return <Pills options={options} value={value} onChange={onChange} disabled={disabled} multi />;
    case 'level':
      return <Level value={value} onChange={onChange} disabled={disabled} />;
    case 'yesno':
      return (
        <Pills
          options={[
            { value: 1, label: 'Yes' },
            { value: 0, label: 'No' },
          ]}
          value={value === null || value === undefined || value === '' ? null : Number(value)}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case 'select':
      return (
        <TextField select size="small" fullWidth value={value ?? ''} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
          <MenuItem value="">
            <em>—</em>
          </MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      );
    default:
      return (
        <TextField
          size="small"
          fullWidth
          disabled={disabled}
          placeholder={field.placeholder}
          type={field.type === 'datetime' ? 'datetime-local' : ['number', 'date'].includes(field.type) ? field.type : 'text'}
          multiline={field.type === 'textarea'}
          minRows={field.type === 'textarea' ? 2 : undefined}
          value={toInput(field.type, value)}
          onChange={(e) => onChange(e.target.value)}
          InputLabelProps={['date', 'datetime'].includes(field.type) ? { shrink: true } : undefined}
        />
      );
  }
}

const NO_VALUES = {};

const isEmpty = (v) => v === null || v === undefined || v === '';

// config: overrides STAGE_FIELDS[stageNo] (the stage 12 exit forms). resetKey: bump to clear edits (e.g. after logging a call).
// prefill: suggested values for fields still empty in `values` — they start as unsaved edits the CRO reviews and saves.
export default function StageForm({
  stageNo,
  config: configOverride,
  values = NO_VALUES,
  prefill = NO_VALUES,
  dynamicOptions,
  onSave,
  saving,
  readOnly,
  extraActions,
  saveLabel,
  resetKey,
  readOnlyLabel,
}) {
  const config = configOverride || STAGE_FIELDS[stageNo];
  const seed = useMemo(
    () => (readOnly ? {} : Object.fromEntries(Object.entries(prefill).filter(([k, v]) => !isEmpty(v) && isEmpty(values[k])))),
    [prefill, values, readOnly],
  );
  const [draft, setDraft] = useState(seed);
  // a fresh server copy (after save / refetch) clears local edits back to the prefill
  // (keep the same empty object so callers passing inline `values` don't re-render in a loop)
  useEffect(() => setDraft((d) => (!Object.keys(seed).length && !Object.keys(d).length ? d : seed)), [seed, resetKey]);

  const merged = useMemo(() => ({ ...values, ...draft }), [values, draft]);
  const dirtyKeys = Object.keys(draft);
  if (!config) return null;

  const set = (key) => (v) => setDraft((d) => ({ ...d, [key]: v }));

  return (
    <Stack gap={2.5}>
      {config.sections.map((section) => (
        <Box key={section.title}>
          <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
            <IconBadge icon={section.icon} size={28} color={section.private ? RT.error : RT.primary} />
            <Typography variant="overline" sx={{ color: section.private ? RT.error : RT.ink2 }}>
              {section.title}
            </Typography>
            {section.private && <SoftChip label="CRO only" icon="solar:lock-keyhole-bold" color={RT.error} sx={{ height: 20, fontSize: 10.5 }} />}
            <Box sx={{ flex: 1, borderTop: `1px dashed ${RT.line}` }} />
          </Stack>
          <Grid container columnSpacing={2} rowSpacing={2}>
            {section.fields.map((field) => (
              <Grid item xs={12} sm={field.half ? 6 : 12} key={field.key}>
                <FieldLabel field={field} prefilled={field.key in seed && draft[field.key] === seed[field.key]} />
                <FieldInput
                  field={field}
                  value={merged[field.key]}
                  onChange={set(field.key)}
                  disabled={readOnly || saving}
                  dynamicOptions={dynamicOptions}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ pt: 1.5, borderTop: `1px solid ${RT.line}`, bgcolor: dirtyKeys.length ? alpha(RT.warning, 0.06) : 'transparent', mx: -2.5, px: 2.5, pb: 0.25 }}
      >
        {readOnly ? (
          <SoftChip
            label={readOnlyLabel || "Preview · saving arrives with this stage's API"}
            icon={readOnlyLabel ? 'solar:lock-keyhole-bold' : 'solar:eye-bold'}
            color={RT.muted}
          />
        ) : dirtyKeys.length ? (
          <SoftChip label={`${dirtyKeys.length} unsaved`} icon="solar:pen-bold" color={RT.warning} />
        ) : null}
        <Box sx={{ flex: 1 }} />
        {extraActions}
        {!readOnly && (
          <>
            <Button size="small" variant="outlined" disabled={!dirtyKeys.length || saving} onClick={() => setDraft({})}>
              Reset
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!dirtyKeys.length || saving}
              startIcon={<Icon icon={saving ? 'svg-spinners:ring-resize' : 'solar:diskette-bold'} width={16} />}
              onClick={() => onSave(Object.fromEntries(dirtyKeys.map((k) => [k, draft[k] === '' ? null : draft[k]])))}
            >
              {saveLabel || config.saveLabel || 'Save'}
            </Button>
          </>
        )}
      </Stack>
    </Stack>
  );
}
